/*       */
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Tooltip, Badge, MdiIcon } from '@mic3/platform-ui';
import { bind } from 'app/utils/decorators/decoratorUtils';
import styled from 'styled-components';

const StyledTextField = styled(TextField)`
    & .MuiInputBase-root {
        font-family: 'Courier New', monospace;
        font-size: 12px;
    }
`;

const BadgeStyled = styled(Badge)`
    width: 100%;
    & .MuiBadge-badge {
        position: relative;
        transform: none;
        background: transparent;
        right: auto;
        top: auto;
    }
`;

const MdiIconStyled = styled(MdiIcon)`
    && {
        overflow: visible;
    }
`;

class RadioValueRawModal extends PureComponent {
    static propTypes = {
        open: PropTypes.bool.isRequired,
        value: PropTypes.arrayOf(
            PropTypes.shape({
                value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                label: PropTypes.string,
                helperText: PropTypes.string,
                icon: PropTypes.string,
            })
        ),
        onClose: PropTypes.func.isRequired,
        onSave: PropTypes.func.isRequired,
    };

    static defaultProps = {
        value: [],
    };

    constructor(props) {
        super(props);
        this.state = {
            rawText: this.valuesToRawText(props.value),
            error: null,
        };
    }

    valuesToRawText(values) {
        if (!values || values.length === 0) {
            return '';
        }
        return values
            .map((item) => {
                const parts = [
                    String(item.value ?? ''),
                    String(item.label ?? '')
                ];

                const iconValue = item.icon?.value !== undefined ? item.icon.value : item.icon;
                const iconType = item.icon?.type || '';

                if (item.helperText) {
                    parts.push(String(item.helperText));
                } else if (iconValue) {
                    parts.push('');
                }

                if (iconValue) {
                    parts.push(String(iconValue));
                    if (iconType) {
                        parts.push(String(iconType));
                    }
                }

                return parts.join(',');
            })
            .join('\n');
    }

    rawTextToValues(text) {
        if (!text || !text.trim()) {
            return [];
        }

        const lines = text.split(/\r\n|\r|\n/g).filter(line => line.trim());
        const values = [];
        const errors = [];

        lines.forEach((line, index) => {
            const parts = line.split(',').map(part => part.trim());

            if (parts.length < 2) {
                errors.push(`Line ${index + 1}: Must have at least value and label`);
                return;
            }

            const [value, label, helperText, iconName, iconType] = parts;

            if (!value) {
                errors.push(`Line ${index + 1}: Value is required`);
                return;
            }

            if (!label) {
                errors.push(`Line ${index + 1}: Label is required`);
                return;
            }

            const item = {
                value: !isNaN(Number(value)) ? Number(value) : value,
                label,
            };

            if (helperText) {
                item.helperText = helperText;
            }

            if (iconName) {
                if (iconType) {
                    item.icon = {
                        value: iconName,
                        label: iconName,
                        type: iconType,
                    };
                } else {
                    item.icon = iconName;
                }
            }

            values.push(item);
        });

        return { values, errors };
    }

    @bind
    handleChange(event) {
        this.setState({
            rawText: event.target.value,
            error: null,
        });
    }

    @bind
    handleSave() {
        const { onSave } = this.props;
        const { rawText } = this.state;

        const result = this.rawTextToValues(rawText);

        if (result.errors && result.errors.length > 0) {
            this.setState({ error: result.errors.join('\n') });
            return;
        }

        onSave(result.values);
    }

    @bind
    handleCancel() {
        const { onClose } = this.props;
        onClose();
    }

    render() {
        const { open } = this.props;
        const { rawText, error } = this.state;

        return (
            <Dialog open={open} onClose={this.handleCancel} maxWidth="md" fullWidth>
                <DialogTitle>Edit Raw</DialogTitle>
                <DialogContent>
                    <BadgeStyled
                        badgeContent={
                            <Tooltip 
                                title={
                                    <div>
                                        <strong>Add CSV text</strong>
                                        <br /><br />
                                        <strong>Format:</strong> Value,Label,Helper text,Icon name,Icon type
                                        <br /><br />
                                        <strong>Example:</strong>
                                        <br />
                                        house,House
                                        <br />
                                        apartment,Apartment,Additional information or warning
                                        <br />
                                        garage,Garage,,home,mdi
                                        <br />
                                        office,Office,,entities,af
                                    </div>
                                } 
                                placement="top"
                            >
                                <span>
                                    <MdiIconStyled size={17} name="help-circle" color="secondary" />
                                </span>
                            </Tooltip>
                        }
                    >
                        <StyledTextField
                            fullWidth
                            multiline
                            rows={15}
                            variant="outlined"
                            value={rawText}
                            onChange={this.handleChange}
                            error={!!error}
                            helperText={error}
                            placeholder="value1,Label 1&#10;value2,Label 2,Helper text&#10;value3,Label 3,Helper text,icon-name,mdi"
                        />
                    </BadgeStyled>
                </DialogContent>
                <DialogActions>
                    <Button onClick={this.handleCancel} color="default">
                        CANCEL
                    </Button>
                    <Button onClick={this.handleSave} color="primary">
                        APPLY
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }
}

export default RadioValueRawModal;
